import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, CheckCircle2, Clock, Lock, PlayCircle, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError } from "@/api/client";

/**
 * Informed OS Academy — program detail page (Phase 1b).
 *
 * Route: `/academy/programs/:slug`.
 *
 * Fetches `GET /api/academy/programs/:slug` (published only) plus
 * `GET /api/academy/enrollments/me` to derive enrolled-state for the
 * CTA. On "Enroll" click POSTs to `/api/academy/enrollments`.
 *
 * "Continue Learning" CTA is intentionally disabled in 1b — the
 * lesson player ships in Phase 1c. Tooltip on the disabled button
 * discloses this.
 */
export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", program: null, error: null, disabled: false });
  const [enrollments, setEnrollments] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [progressMap, setProgressMap] = useState([]);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const [progRes, enrRes] = await Promise.all([
        api.get(`/academy/programs/${slug}`),
        api.get(`/academy/enrollments/me`).catch(() => ({ data: { items: [] } })),
      ]);
      setState({ status: "ready", program: progRes.data, error: null, disabled: false });
      setEnrollments(enrRes.data?.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (err?.response?.status === 404 && code === "feature_disabled") {
        setState({ status: "disabled", program: null, error: null, disabled: true });
        return;
      }
      if (err?.response?.status === 404) {
        setState({ status: "not_found", program: null, error: null, disabled: false });
        return;
      }
      setState({ status: "error", program: null, error: formatApiError(err), disabled: false });
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const activeEnrollment = useMemo(() => {
    if (!state.program) return null;
    return enrollments.find(
      (e) => e.program_id === state.program.id && (e.status === "active" || e.status === "completed"),
    ) || null;
  }, [enrollments, state.program]);

  // Phase 1c — fetch per-lesson progress map for the enrolled user so
  // completion checkmarks appear on the curriculum list.
  useEffect(() => {
    if (!activeEnrollment) { setProgressMap([]); return; }
    let cancelled = false;
    api.get(`/academy/enrollments/${activeEnrollment.id}/progress`)
      .then((r) => { if (!cancelled) setProgressMap(r.data?.items || []); })
      .catch(() => { if (!cancelled) setProgressMap([]); });
    return () => { cancelled = true; };
  }, [activeEnrollment]);

  const progressByLessonId = useMemo(() => {
    const m = new Map();
    for (const item of progressMap) m.set(item.id, item);
    return m;
  }, [progressMap]);

  // Continue Learning target — server-supplied current_lesson via the
  // dashboard endpoint would be one route, but we can derive it here
  // by preferring the enrollment's `current_lesson_id` (matched against
  // the program's published lessons) and falling back to the first
  // published lesson.
  const continueLessonSlug = useMemo(() => {
    if (!state.program || !activeEnrollment) return null;
    const currentId = activeEnrollment.current_lesson_id;
    if (currentId) {
      for (const mod of state.program.modules || []) {
        for (const l of mod.lessons || []) {
          if (l.id === currentId) return l.slug;
        }
      }
    }
    for (const mod of state.program.modules || []) {
      for (const l of mod.lessons || []) return l.slug;
    }
    return null;
  }, [state.program, activeEnrollment]);

  const onEnroll = useCallback(async () => {
    if (!state.program) return;
    setEnrolling(true);
    try {
      const res = await api.post(`/academy/enrollments`, { program_id: state.program.id });
      toast.success("Enrolled — you can now start when the lessons open.");
      // Refresh enrollments cache.
      const enrRes = await api.get(`/academy/enrollments/me`).catch(() => ({ data: { items: [] } }));
      setEnrollments(enrRes.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Enrollment failed. Try again.");
    } finally {
      setEnrolling(false);
    }
  }, [state.program]);

  if (state.status === "loading") {
    return (
      <ShellWithNav>
        <div data-testid="academy-program-loading" className="text-v2-text-muted">Loading program…</div>
      </ShellWithNav>
    );
  }
  if (state.status === "disabled") {
    return (
      <ShellWithNav>
        <div
          data-testid="academy-program-disabled"
          className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
        >
          <p className="text-v2-text font-semibold">Academy is not yet available.</p>
          <p className="mt-3 text-sm text-v2-text-muted">Please check back later.</p>
          <Link
            to="/dashboard"
            data-testid="academy-program-disabled-back"
            className="mt-6 inline-flex items-center gap-2 text-sm text-v2-accent hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
        </div>
      </ShellWithNav>
    );
  }
  if (state.status === "not_found") {
    return (
      <ShellWithNav>
        <div
          data-testid="academy-program-not-found"
          className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
        >
          <p className="text-v2-text font-semibold">Program not found.</p>
          <p className="mt-3 text-sm text-v2-text-muted">
            The program you're looking for isn't available or has been unpublished.
          </p>
          <Link
            to="/academy"
            data-testid="academy-program-nf-back"
            className="mt-6 inline-flex items-center gap-2 text-sm text-v2-accent hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Academy
          </Link>
        </div>
      </ShellWithNav>
    );
  }
  if (state.status === "error") {
    return (
      <ShellWithNav>
        <div
          data-testid="academy-program-error"
          className="rounded-xl border border-[var(--v2-danger)] bg-v2-surface p-6 text-v2-text"
        >
          <p className="font-semibold text-[var(--v2-danger)]">Couldn't load program.</p>
          <p className="mt-2 text-sm text-v2-text-muted">{state.error}</p>
          <button
            data-testid="academy-program-retry"
            type="button"
            onClick={load}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-v2-border text-sm hover:bg-v2-surface-muted"
          >
            Retry
          </button>
        </div>
      </ShellWithNav>
    );
  }

  const p = state.program;
  return (
    <ShellWithNav testId="academy-program-detail">
      <Link
        to="/academy"
        data-testid="academy-program-back"
        className="inline-flex items-center gap-2 text-sm text-v2-text-muted hover:text-v2-text mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        All programs
      </Link>

      <header className="mb-10 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="min-w-0">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border"
            style={{
              background: "var(--v2-accent-soft)",
              borderColor: "var(--v2-accent)",
              color: "var(--v2-accent)",
            }}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            Certificate program
          </div>
          <h1
            data-testid="academy-program-title"
            className="mt-6 text-[32px] md:text-[42px] font-semibold tracking-tight text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {p.title}
          </h1>
          {p.subtitle ? (
            <p className="mt-3 text-[16px] text-v2-text-muted max-w-2xl leading-relaxed">
              {p.subtitle}
            </p>
          ) : null}
        </div>
        <div className="shrink-0 md:min-w-[220px] flex flex-col gap-3">
          {activeEnrollment ? (
            continueLessonSlug ? (
              <Link
                to={`/academy/programs/${p.slug}/lessons/${continueLessonSlug}`}
                data-testid="academy-program-continue-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90"
              >
                <PlayCircle className="w-4 h-4" />
                Continue Learning
              </Link>
            ) : (
              <button
                type="button"
                disabled
                data-testid="academy-program-continue-btn"
                title="No published lessons yet — check back soon."
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-v2-border bg-v2-surface-muted text-v2-text-muted cursor-not-allowed"
              >
                <Lock className="w-4 h-4" />
                Continue Learning
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={onEnroll}
              disabled={enrolling}
              data-testid="academy-program-enroll-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <PlayCircle className="w-4 h-4" />
              {enrolling ? "Enrolling…" : "Enroll"}
            </button>
          )}
          {activeEnrollment ? (
            <div
              data-testid="academy-program-enrolled-chip"
              className="inline-flex items-center gap-2 justify-center text-xs text-[var(--v2-tier-ready)]"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Enrolled · {activeEnrollment.program_progress_pct ?? activeEnrollment.progress_pct ?? 0}% complete
            </div>
          ) : null}
        </div>
      </header>

      {p.description_markdown ? (
        <section className="mb-10 rounded-xl border border-v2-border bg-v2-surface p-6 md:p-8">
          <h2 className="text-[13px] uppercase tracking-[0.15em] text-v2-text-muted mb-4">About this program</h2>
          <div className="prose prose-sm max-w-none text-v2-text whitespace-pre-wrap">
            {p.description_markdown}
          </div>
        </section>
      ) : null}

      {p.learning_outcomes && p.learning_outcomes.length > 0 ? (
        <section data-testid="academy-program-outcomes" className="mb-10">
          <h2 className="text-[13px] uppercase tracking-[0.15em] text-v2-text-muted mb-4">You'll learn</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {p.learning_outcomes.map((outcome, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm text-v2-text">
                <CheckCircle2 className="w-4 h-4 text-[var(--v2-tier-ready)] flex-shrink-0 mt-0.5" />
                <span>{outcome}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section data-testid="academy-program-curriculum">
        <h2 className="text-[13px] uppercase tracking-[0.15em] text-v2-text-muted mb-4">
          Curriculum · {p.module_count ?? 0} module{p.module_count === 1 ? "" : "s"} · {p.lesson_count ?? 0} lesson{p.lesson_count === 1 ? "" : "s"}
        </h2>
        {p.modules && p.modules.length > 0 ? (
          <ol className="space-y-4">
            {p.modules.map((mod, idx) => (
              <li
                key={mod.id}
                data-testid={`academy-program-module-${mod.slug}`}
                className="rounded-xl border border-v2-border bg-v2-surface p-6"
              >
                <div className="flex items-baseline gap-3">
                  <span className="text-xs uppercase tracking-widest text-v2-text-muted">Module {idx + 1}</span>
                </div>
                <h3
                  className="mt-2 text-[18px] font-semibold text-v2-text"
                  style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                >
                  {mod.title}
                </h3>
                {mod.description ? (
                  <p className="mt-2 text-sm text-v2-text-muted">{mod.description}</p>
                ) : null}
                {mod.lessons && mod.lessons.length > 0 ? (
                  <ul className="mt-4 space-y-2 border-t border-v2-border pt-4">
                    {mod.lessons.map((lesson) => {
                      const lp = progressByLessonId.get(lesson.id);
                      const done = lp?.progress?.status === "completed";
                      const inner = (
                        <>
                          {done ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-[var(--v2-tier-ready)] flex-shrink-0" />
                          ) : (
                            <BookOpen className="w-3.5 h-3.5 text-v2-text-muted flex-shrink-0" />
                          )}
                          <span className="flex-1 truncate">{lesson.title}</span>
                          {lesson.estimated_minutes ? (
                            <span className="text-xs text-v2-text-muted flex-shrink-0">
                              {lesson.estimated_minutes} min
                            </span>
                          ) : null}
                        </>
                      );
                      return (
                        <li
                          key={lesson.id}
                          data-testid={`academy-program-lesson-${lesson.slug}`}
                          className="flex items-center gap-3 text-sm text-v2-text"
                        >
                          {activeEnrollment ? (
                            <Link
                              to={`/academy/programs/${p.slug}/lessons/${lesson.slug}`}
                              data-testid={`academy-program-lesson-link-${lesson.slug}`}
                              className="flex items-center gap-3 w-full hover:text-v2-accent"
                            >
                              {inner}
                            </Link>
                          ) : (
                            <span className="flex items-center gap-3 w-full">{inner}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p
                    data-testid={`academy-program-module-empty-${mod.slug}`}
                    className="mt-4 text-xs text-v2-text-muted italic"
                  >
                    Lessons coming soon.
                  </p>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p data-testid="academy-program-no-modules" className="text-sm text-v2-text-muted italic">
            Curriculum coming soon.
          </p>
        )}
      </section>
    </ShellWithNav>
  );
}

function ShellWithNav({ children, testId = "academy-program-detail-shell" }) {
  return (
    <div
      data-testid={testId}
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-16 pb-24">
        {children}
      </main>
    </div>
  );
}
